import { supabase } from '@/integrations/supabase/client';

export interface AnalyticsEvent {
  user_id?: string;
  session_id: string;
  event_type: string;
  event_data?: Record<string, any>;
  page_path?: string;
  event_id?: string;
  user_agent?: string;
  ip_address?: string;
}


export interface EventStatistics {
  event_id: string;
  total_views: number;
  unique_views: number;
  click_through_rate: number;
  conversion_rate: number;
}

export interface UserAnalytics {
  user_id: string;
  total_page_views: number;
  session_count: number;
  avg_session_duration: number;
  last_active: string;
  registration_date: string;
}

export interface DashboardAnalytics {
  total_events: number;
  total_users: number;
  popular_events: Array<{
    event_id: string;
    views: number;
  }>;
  page_views_trend: Array<{
    date: string;
    views: number;
  }>;
}

class SupabaseAnalyticsService {
  private currentSessionId: string | null = null;

  // Generate or get current session ID
  getSessionId(): string {
    if (!this.currentSessionId) {
      this.currentSessionId = crypto.randomUUID();
    }
    return this.currentSessionId;
  }


  // Get user's IP address
  private async getUserIP(): Promise<string | null> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.warn('Could not fetch IP address:', error);
      return null;
    }
  }

  // Track analytics event
  async trackEvent(event: Omit<AnalyticsEvent, 'session_id'>): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const ipAddress = await this.getUserIP();

      const eventData = {
        ...event,
        session_id: this.getSessionId(),
        user_id: user?.id || undefined,
        user_agent: navigator.userAgent,
        ip_address: ipAddress,
      };

      const { error } = await supabase
        .from('analytics_events')
        .insert([eventData]);

      if (error) {
        console.error('Error tracking event:', error);
      }
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }

  // Track page view
  async trackPageView(page_path: string, event_id?: string): Promise<void> {
    await this.trackEvent({
      event_type: 'page_view',
      page_path,
      event_id,
      event_data: { timestamp: new Date().toISOString() }
    });
  }

  // Track event view
  async trackEventView(event_id: string): Promise<void> {
    await this.trackEvent({
      event_type: 'event_view',
      event_id,
      event_data: { timestamp: new Date().toISOString() }
    });

    // Update event statistics
    await this.updateEventStatistics(event_id);
  }

  // Track event click
  async trackEventClick(event_id: string, click_type: string = 'general'): Promise<void> {
    await this.trackEvent({
      event_type: 'event_click',
      event_id,
      event_data: {
        click_type,
        timestamp: new Date().toISOString()
      }
    });
  }




  // Update event statistics
  private async updateEventStatistics(event_id: string): Promise<void> {
    console.log('📊 Updating event statistics for eventId:', event_id);
    try {
      // Manual upsert approach since RPC function might not exist yet
      try {
        const { data: existing, error: selectError } = await supabase
          .from('event_statistics')
          .select('*')
          .eq('event_id', event_id)
          .maybeSingle();

        console.log('📊 Existing stats for event:', event_id, existing, 'error:', selectError);

        if (existing) {
          const { data: updateData, error: updateError } = await supabase
            .from('event_statistics')
            .update({
              total_views: (existing.total_views || 0) + 1,
              last_updated: new Date().toISOString(),
            })
            .eq('event_id', event_id);

          console.log('📊 Updated stats:', updateData, 'error:', updateError);
        } else {
          const { data: insertData, error: insertError } = await supabase
            .from('event_statistics')
            .insert([{
              event_id,
              total_views: 1,
              unique_views: 1,
            }]);

          console.log('📊 Inserted new stats:', insertData, 'error:', insertError);
        }
      } catch (updateError) {
        console.error('📊 Error in manual upsert:', updateError);
      }
    } catch (error) {
      console.error('📊 Error updating event statistics:', error);
    }
  }

  // Get dashboard analytics
  async getDashboardAnalytics(timeRange: string = 'week'): Promise<DashboardAnalytics | null> {
    try {
      const timeFilter = this.getTimeFilter(timeRange);

      // Get total counts
      const [eventsCount, usersCount] = await Promise.all([
        supabase.from('analytics_events').select('*', { count: 'exact', head: true }),
        supabase.from('user_analytics').select('*', { count: 'exact', head: true })
      ]);

      // Get popular events
      const { data: popularEvents } = await supabase
        .from('event_statistics')
        .select('event_id, total_views')
        .order('total_views', { ascending: false })
        .limit(10);

      // Get page views trend
      const { data: pageViewsTrend } = await supabase
        .from('analytics_events')
        .select('created_at')
        .eq('event_type', 'page_view')
        .gte('created_at', timeFilter)
        .order('created_at');

      const trendData = this.aggregateByDay(pageViewsTrend || []);

      return {
        total_events: eventsCount.count || 0,
        total_users: usersCount.count || 0,
        popular_events: popularEvents?.map(e => ({ event_id: e.event_id, views: e.total_views || 0 })) || [],
        page_views_trend: trendData,
      };
    } catch (error) {
      console.error('Error fetching dashboard analytics:', error);
      return null;
    }
  }

  // Get event analytics
  async getEventAnalytics(event_id: string): Promise<EventStatistics | null> {
    try {
      const { data, error } = await supabase
        .from('event_statistics')
        .select('*')
        .eq('event_id', event_id)
        .single();

      if (error || !data) {
        return null;
      }

      return {
        event_id: data.event_id,
        total_views: data.total_views || 0,
        unique_views: data.unique_views || 0,
        click_through_rate: data.click_through_rate || 0,
        conversion_rate: data.conversion_rate || 0,
      };
    } catch (error) {
      console.error('Error fetching event analytics:', error);
      return null;
    }
  }

  // Get user analytics
  async getUserAnalytics(user_id: string): Promise<UserAnalytics | null> {
    try {
      const { data, error } = await supabase
        .from('user_analytics')
        .select('*')
        .eq('user_id', user_id)
        .single();

      if (error || !data) {
        return null;
      }

      return {
        user_id: data.user_id,
        total_page_views: data.total_page_views || 0,
        session_count: data.session_count || 0,
        avg_session_duration: data.avg_session_duration || 0,
        last_active: data.last_active || new Date().toISOString(),
        registration_date: data.registration_date || new Date().toISOString(),
      };
    } catch (error) {
      console.error('Error fetching user analytics:', error);
      return null;
    }
  }

  // Helper methods
  private getTimeFilter(timeRange: string): string {
    const now = new Date();
    switch (timeRange) {
      case 'day':
        return new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      case 'week':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      case 'month':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      case 'year':
        return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString();
      default:
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    }
  }

  private aggregateByDay(events: Array<{ created_at: string }>): Array<{ date: string; views: number }> {
    const grouped = events.reduce((acc, event) => {
      const date = new Date(event.created_at).toISOString().split('T')[0];
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(grouped)
      .map(([date, views]) => ({ date, views }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }
}

export const supabaseAnalyticsService = new SupabaseAnalyticsService();
export default supabaseAnalyticsService;